// /src/webrtc/voiceCall.js
import { rtdb } from "../firebase";
import {
  ref as rtdbRef,
  onValue,
  set,
  remove,
  push,
  update,
  onChildAdded
} from "firebase/database";

let localStream = null;
let peerConnection = null;
let currentCameraDeviceId = null;
let currentFacingMode = 'user';

// Processing Locks
let isSettingRemoteAnswer = false;

// Listeners
let unsubCallListener = null;
let unsubCandidateListener = null;

export async function startVoiceCall(convId, currentUid, remoteUid, isVideo = false, onRemoteStream, onCallEnd) {
  // Ensure fresh state
  cleanup();

  const servers = {
    iceServers: [{ urls: "stun:stun.l.google.com:19302" }],
  };

  peerConnection = new RTCPeerConnection(servers);
  isSettingRemoteAnswer = false; // Reset lock

  try {
    localStream = await navigator.mediaDevices.getUserMedia({
      audio: true,
      video: isVideo ? { facingMode: "user" } : false
    });
  } catch (err) {
    console.error("Error accessing media devices:", err);
    throw err;
  }

  // Add tracks
  if (localStream && peerConnection) {
    localStream.getTracks().forEach((track) => {
      if (peerConnection) peerConnection.addTrack(track, localStream);
      if (track.kind === 'video') {
        currentCameraDeviceId = track.getSettings().deviceId;
      }
    });
  }

  if (peerConnection) {
    peerConnection.ontrack = (event) => {
      if (onRemoteStream && event.streams && event.streams[0]) {
        onRemoteStream(event.streams[0]);
      }
    };

    peerConnection.oniceconnectionstatechange = () => {
      if (peerConnection && (peerConnection.iceConnectionState === "disconnected" || peerConnection.iceConnectionState === "failed")) {
        console.warn("ICE connection failed or disconnected.");
      }
    };
  }

  const callRef = rtdbRef(rtdb, `calls/${convId}`);
  const offerCandidatesRef = rtdbRef(rtdb, `calls/${convId}/offerCandidates`);
  const answerCandidatesRef = rtdbRef(rtdb, `calls/${convId}/answerCandidates`);

  if (peerConnection) {
    peerConnection.onicecandidate = (event) => {
      if (event.candidate) {
        const candidateRef = push(offerCandidatesRef);
        set(candidateRef, {
          uid: currentUid,
          candidate: event.candidate.toJSON(),
        });
      }
    };

    const offer = await peerConnection.createOffer({
      offerToReceiveAudio: true,
      offerToReceiveVideo: true
    });
    await peerConnection.setLocalDescription(offer);

    await set(callRef, {
      offer: {
        type: offer.type,
        sdp: offer.sdp,
      },
      caller: currentUid,
      receiver: remoteUid,
      isVideo: isVideo,
      status: 'dialing',
      timestamp: Date.now()
    });
  }

  const startTime = Date.now();
  const candidateQueue = [];

  // Listener for Answer
  if (unsubCallListener) unsubCallListener();
  unsubCallListener = onValue(callRef, async (snapshot) => {
    if (!peerConnection) return;

    const data = snapshot.val();
    console.log("📞 WebRTC Signaling Data:", data);

    if (!data) {
      if (Date.now() - startTime < 3000) {
        console.warn("⚠️ Ignoring null data (Grace Period)");
        return;
      }
      console.log("❌ Call Node Removed (ENDED)");
      cleanup();
      if (onCallEnd) onCallEnd("ENDED");
      return;
    }

    if (data.status === 'rejected') {
      cleanup();
      if (onCallEnd) onCallEnd("REJECTED");
      return;
    }
    if (data.status === 'ended') {
      cleanup();
      if (onCallEnd) onCallEnd("ENDED");
      return;
    }

    // Handle Remote Answer
    if (data.answer && peerConnection && !peerConnection.currentRemoteDescription) {
      // Prevent race conditions
      if (peerConnection.signalingState === 'stable') return;
      if (peerConnection.signalingState !== 'have-local-offer') {
        return;
      }
      if (isSettingRemoteAnswer) return;

      isSettingRemoteAnswer = true;
      try {
        const answerDesc = new RTCSessionDescription(data.answer);
        if (peerConnection) await peerConnection.setRemoteDescription(answerDesc);

        // Process queued candidates
        while (candidateQueue.length > 0) {
          const c = candidateQueue.shift();
          try {
            if (peerConnection) await peerConnection.addIceCandidate(new RTCIceCandidate(c));
          } catch (err) {
            console.warn("Failed to add queued candidate:", err);
          }
        }
      } catch (error) {
        console.error("Error setting remote description:", error);
      } finally {
        isSettingRemoteAnswer = false;
      }
    }
  });

  if (unsubCandidateListener) unsubCandidateListener();
  unsubCandidateListener = onChildAdded(answerCandidatesRef, (snapshot) => {
    if (!peerConnection) return;

    const data = snapshot.val();
    const c = data?.candidate ? data : { candidate: data };

    if (c && c.candidate) {
      if (peerConnection && peerConnection.remoteDescription && peerConnection.signalingState !== 'closed') {
        peerConnection.addIceCandidate(new RTCIceCandidate(c.candidate)).catch(err => {
          console.warn("Add ICE error", err);
        });
      } else {
        candidateQueue.push(c.candidate);
      }
    }
  });

  return localStream;
}

export function toggleAudio(enabled) {
  if (localStream) {
    localStream.getAudioTracks().forEach(track => {
      track.enabled = enabled;
    });
  }
}

export function toggleVideo(enabled) {
  if (localStream) {
    localStream.getVideoTracks().forEach(track => {
      track.enabled = enabled;
    });
  }
}

export const switchCamera = async () => {
  // ... (Keep existing implementation, but check peerConnection validity if needed)
  // The existing implementation checks peerConnection in step 4.
  // I won't rewrite this whole function to save tokens, assuming it's fine.
  // I can just include the original content or skip replacing this part if I use Start/End lines carefully.
  // But replace_file_content is full block replacement if I don't specify limits.
  // I see the original file has 278 lines.
  // I will replace up to line 166 (end of startVoiceCall) and then lines 255 (cleanup).
  // Or just rewrite the whole file to be safe. It's not too huge.
  // Wait, switchCamera lines 184-253.
  // I will recreate switchCamera too.
  if (!localStream) return null;
  const oldVideoTrack = localStream.getVideoTracks()[0];

  // 1. Determine new facing mode
  let newFacingMode = 'user';
  if (oldVideoTrack) {
    const settings = oldVideoTrack.getSettings();
    if (settings.facingMode) {
      newFacingMode = settings.facingMode === 'user' ? 'environment' : 'user';
    } else {
      newFacingMode = currentFacingMode === 'user' ? 'environment' : 'user';
    }
  }
  currentFacingMode = newFacingMode;


  // 2. STOP OLD TRACK FIRST (Mobile Fix)
  // Many android devices cannot handle two open camera streams at once.
  if (oldVideoTrack) {
    oldVideoTrack.stop();
  }

  let newStream = null;

  try {
    // 3. Request new stream
    newStream = await navigator.mediaDevices.getUserMedia({
      video: { facingMode: { exact: newFacingMode } },
      audio: false
    }).catch(async () => {
      // Fallback
      return await navigator.mediaDevices.getUserMedia({
        video: { facingMode: newFacingMode },
        audio: false
      });
    });
  } catch (err) {
    console.warn("Camera switch failed:", err);
    return null;
  }

  if (newStream) {
    const newVideoTrack = newStream.getVideoTracks()[0];

    // 4. Update Sender (WebRTC)
    if (peerConnection) {
      const sender = peerConnection.getSenders().find(s => s.track && s.track.kind === 'video');
      if (sender) {
        await sender.replaceTrack(newVideoTrack).catch(e => console.error("replaceTrack failed", e));
      }
    }

    // 5. Update Local Stream
    // Remove all old video tracks (should be stopped already)
    // We filter out the NEW track just in case it was added already (duplicate safety)
    const oldTracks = localStream.getVideoTracks().filter(t => t.id !== newVideoTrack.id);
    oldTracks.forEach(track => {
      track.stop();
      localStream.removeTrack(track);
    });
    localStream.addTrack(newVideoTrack);

    // 6. Return Clone
    const clonedStream = new MediaStream(localStream.getTracks());
    localStream = clonedStream;
    return clonedStream;
  }
  return null;
};

function cleanup() {
  if (unsubCallListener) {
    unsubCallListener();
    unsubCallListener = null;
  }
  if (unsubCandidateListener) {
    unsubCandidateListener();
    unsubCandidateListener = null;
  }

  if (peerConnection) {
    peerConnection.onicecandidate = null;
    peerConnection.ontrack = null;
    peerConnection.close();
    peerConnection = null;
  }
  if (localStream) {
    localStream.getTracks().forEach((track) => track.stop());
    localStream = null;
  }
  isSettingRemoteAnswer = false;
}

export function endCall(convId) {
  cleanup();
  if (convId) {
    const callRef = rtdbRef(rtdb, `calls/${convId}`);
    update(callRef, { status: 'ended' }).then(() => {
      setTimeout(() => {
        remove(callRef);
      }, 2000);
    });
  }
}
